/* istanbul ignore file */
/* tslint:disable */
 
import type { BuildingConstruction } from '../models/BuildingConstruction';
import type { BuildingDestination } from '../models/BuildingDestination';
import type { BuildingProtection } from '../models/BuildingProtection';
import type { LandProtection } from '../models/LandProtection';
import type { PaginatedBuildingConstructionList } from '../models/PaginatedBuildingConstructionList';
import type { PaginatedBuildingDestinationList } from '../models/PaginatedBuildingDestinationList';
import type { PaginatedBuildingProtectionList } from '../models/PaginatedBuildingProtectionList';
import type { PaginatedLandProtectionList } from '../models/PaginatedLandProtectionList';
import type { PaginatedPropertyBorderoList } from '../models/PaginatedPropertyBorderoList';
import type { PaginatedPropertyTypeGetList } from '../models/PaginatedPropertyTypeGetList';
import type { PatchedBuildingConstruction } from '../models/PatchedBuildingConstruction';
import type { PatchedBuildingDestination } from '../models/PatchedBuildingDestination';
import type { PatchedBuildingProtection } from '../models/PatchedBuildingProtection';
import type { PatchedLandProtection } from '../models/PatchedLandProtection';
import type { PatchedPropertyCoefficient } from '../models/PatchedPropertyCoefficient';
import type { PatchedPropertyRisk } from '../models/PatchedPropertyRisk';
import type { PatchedPropertyType } from '../models/PatchedPropertyType';
import type { PropertyCoefficient } from '../models/PropertyCoefficient';
import type { PropertyCoefficientGet } from '../models/PropertyCoefficientGet';
import type { PropertyRisk } from '../models/PropertyRisk';
import type { PropertyRiskGet } from '../models/PropertyRiskGet';
import type { PropertyType } from '../models/PropertyType';
import type { PropertyTypeGet } from '../models/PropertyTypeGet';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class PropertyService {

  /**
   * Bordero base for internal products built on Policy model.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: order__policy__number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns PaginatedPropertyBorderoList 
   * @throws ApiError
   */
  public static propertyBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedPropertyBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Bordero base for internal products built on Policy model.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search Search fields: order__policy__number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static propertyBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param ordering Ordering fields: code, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedBuildingConstructionList 
   * @throws ApiError
   */
  public static propertyBuildingConstructionsTypesList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedBuildingConstructionList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-constructions-types/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param requestBody 
   * @returns BuildingConstruction 
   * @throws ApiError
   */
  public static propertyBuildingConstructionsTypesCreate(
requestBody: BuildingConstruction,
): CancelablePromise<BuildingConstruction> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/property/building-constructions-types/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building construction.
   * @returns BuildingConstruction 
   * @throws ApiError
   */
  public static propertyBuildingConstructionsTypesRetrieve(
code: string,
): CancelablePromise<BuildingConstruction> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-constructions-types/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building construction.
   * @param requestBody 
   * @returns BuildingConstruction 
   * @throws ApiError
   */
  public static propertyBuildingConstructionsTypesPartialUpdate(
code: string,
requestBody?: PatchedBuildingConstruction,
): CancelablePromise<BuildingConstruction> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/building-constructions-types/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building construction.
   * @returns void 
   * @throws ApiError
   */
  public static propertyBuildingConstructionsTypesDestroy(
code: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/property/building-constructions-types/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param ordering Ordering fields: code, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedBuildingDestinationList 
   * @throws ApiError
   */
  public static propertyBuildingDestinationsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedBuildingDestinationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-destinations/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param requestBody 
   * @returns BuildingDestination 
   * @throws ApiError
   */
  public static propertyBuildingDestinationsCreate(
requestBody: BuildingDestination,
): CancelablePromise<BuildingDestination> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/property/building-destinations/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building destination.
   * @returns BuildingDestination 
   * @throws ApiError
   */
  public static propertyBuildingDestinationsRetrieve(
code: string,
): CancelablePromise<BuildingDestination> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-destinations/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building destination.
   * @param requestBody 
   * @returns BuildingDestination 
   * @throws ApiError
   */
  public static propertyBuildingDestinationsPartialUpdate(
code: string,
requestBody?: PatchedBuildingDestination,
): CancelablePromise<BuildingDestination> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/building-destinations/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building destination.
   * @returns void 
   * @throws ApiError
   */
  public static propertyBuildingDestinationsDestroy(
code: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/property/building-destinations/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param ordering Ordering fields: code, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedBuildingProtectionList 
   * @throws ApiError
   */
  public static propertyBuildingProtectionsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedBuildingProtectionList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-protections/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param requestBody 
   * @returns BuildingProtection 
   * @throws ApiError
   */
  public static propertyBuildingProtectionsCreate(
requestBody: BuildingProtection,
): CancelablePromise<BuildingProtection> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/property/building-protections/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building protection.
   * @returns BuildingProtection 
   * @throws ApiError
   */
  public static propertyBuildingProtectionsRetrieve(
code: string,
): CancelablePromise<BuildingProtection> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/building-protections/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building protection.
   * @param requestBody 
   * @returns BuildingProtection 
   * @throws ApiError
   */
  public static propertyBuildingProtectionsPartialUpdate(
code: string,
requestBody?: PatchedBuildingProtection,
): CancelablePromise<BuildingProtection> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/building-protections/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this building protection.
   * @returns void 
   * @throws ApiError
   */
  public static propertyBuildingProtectionsDestroy(
code: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/property/building-protections/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param ordering Ordering fields: id, name
   * @returns PropertyCoefficientGet 
   * @throws ApiError
   */
  public static propertyCoefficientsList(
ordering?: string,
): CancelablePromise<Array<PropertyCoefficientGet>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/coefficients/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this property coefficient.
   * @returns PropertyCoefficientGet 
   * @throws ApiError
   */
  public static propertyCoefficientsRetrieve(
id: number,
): CancelablePromise<PropertyCoefficientGet> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/coefficients/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this property coefficient.
   * @param requestBody 
   * @returns PropertyCoefficient 
   * @throws ApiError
   */
  public static propertyCoefficientsPartialUpdate(
id: number,
requestBody?: PatchedPropertyCoefficient,
): CancelablePromise<PropertyCoefficient> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/coefficients/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param ordering Ordering fields: code, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedLandProtectionList 
   * @throws ApiError
   */
  public static propertyLandProtectionsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedLandProtectionList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/land-protections/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param requestBody 
   * @returns LandProtection 
   * @throws ApiError
   */
  public static propertyLandProtectionsCreate(
requestBody: LandProtection,
): CancelablePromise<LandProtection> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/property/land-protections/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this land protection.
   * @returns LandProtection 
   * @throws ApiError
   */
  public static propertyLandProtectionsRetrieve(
code: string,
): CancelablePromise<LandProtection> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/land-protections/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this land protection.
   * @param requestBody 
   * @returns LandProtection 
   * @throws ApiError
   */
  public static propertyLandProtectionsPartialUpdate(
code: string,
requestBody?: PatchedLandProtection,
): CancelablePromise<LandProtection> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/land-protections/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this land protection.
   * @returns void 
   * @throws ApiError
   */
  public static propertyLandProtectionsDestroy(
code: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/property/land-protections/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param category * `A` - Clădiri și construcții
 * * `B` - Finisajul încăperilor
 * * `C` - Vitrine, vitralii și afișele de reclamă
 * * `D` - Aparate electrice și tehnică electronică
 * * `E` - Mobilă, inventar de producție și gospodărie
 * * `F` - Mărfuri și materiale la depozit
 * * `G` - Utilaj de producție
 * * `H` - Mărfuri și materiale din săli comerciale
 * * `I` - Altele
 * * `J` - Terenuri
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @returns PropertyRiskGet 
   * @throws ApiError
   */
  public static propertyRisksList(
category?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J',
excludeIds?: string,
ids?: string,
): CancelablePromise<Array<PropertyRiskGet>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/risks/',
      query: {
        'category': category,
        'exclude_ids': excludeIds,
        'ids': ids,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this property risk.
   * @returns PropertyRiskGet 
   * @throws ApiError
   */
  public static propertyRisksRetrieve(
id: number,
): CancelablePromise<PropertyRiskGet> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/risks/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this property risk.
   * @param requestBody 
   * @returns PropertyRisk 
   * @throws ApiError
   */
  public static propertyRisksPartialUpdate(
id: number,
requestBody?: PatchedPropertyRisk,
): CancelablePromise<PropertyRisk> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/risks/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param category * `A` - Clădiri și construcții
 * * `B` - Finisajul încăperilor
 * * `C` - Vitrine, vitralii și afișele de reclamă
 * * `D` - Aparate electrice și tehnică electronică
 * * `E` - Mobilă, inventar de producție și gospodărie
 * * `F` - Mărfuri și materiale la depozit
 * * `G` - Utilaj de producție
 * * `H` - Mărfuri și materiale din săli comerciale
 * * `I` - Altele
 * * `J` - Terenuri
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: code, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedPropertyTypeGetList 
   * @throws ApiError
   */
  public static propertyTypesList(
category?: Array<'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J'>,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedPropertyTypeGetList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/types/',
      query: {
        'category': category,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param requestBody 
   * @returns PropertyType 
   * @throws ApiError
   */
  public static propertyTypesCreate(
requestBody: PropertyType,
): CancelablePromise<PropertyType> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/property/types/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this property type.
   * @returns PropertyTypeGet 
   * @throws ApiError
   */
  public static propertyTypesRetrieve(
code: string,
): CancelablePromise<PropertyTypeGet> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/property/types/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this property type.
   * @param requestBody 
   * @returns PropertyType 
   * @throws ApiError
   */
  public static propertyTypesPartialUpdate(
code: string,
requestBody?: PatchedPropertyType,
): CancelablePromise<PropertyType> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/property/types/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet for SimpleReference
   * @param code A unique value identifying this property type.
   * @returns void 
   * @throws ApiError
   */
  public static propertyTypesDestroy(
code: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/property/types/{code}/',
      path: {
        'code': code,
      },
    });
  }

}
